import type { Resource } from '__types__';
import { findSameScopeCustomFieldRow } from 'v5/core/custom-fields/resolveCustomFieldValue';
import { valueScopeUtils } from 'v5/core/form-schema/formSchema.types';
import type {
  GroupRowWrite,
  ResourcePatch,
  ResourcePatchOf
} from 'v5/core/entity-values/resourcePatch.types';
import {
  applyCoreChanges,
  assertPersistedId,
  customFieldRowWithoutId,
  locationRow,
  optionalIdProps,
  rateValueColumns
} from './bodyShared';
import type {
  ContactDetailBodyRow,
  ContactRateBodyRow,
  ContactUpdateBody,
  CustomFieldArchiveRow,
  CustomFieldBodyRow,
  CustomFieldGroupBodyRow,
  GroupBodyRow,
  KeepAliveStub,
  LocationBodyRow,
  TagBodyRow
} from './contactBody.types';

type CurrentCustomField = Resource['custom_fields'][number];
type CurrentCustomFieldGroup = Resource['custom_fieldgroups'][number];

/**
 * Pure: one `ResourcePatch` → one `PUT /contacts/:id` body, encoded against
 * the Objection rules of `Contact.defaultUpdateGraphOptions`
 * (`.agents/kickoff/plan-2026-09-16-v1-1816-v5-diagnosis.md` §1.1). `current`
 * supplies the contact id, the resource type and the keep-alive stubs for
 * custom-field rows.
 */
export function buildUpdateBody(
  patch: ResourcePatch,
  current: Resource
): ContactUpdateBody {
  switch (patch.kind) {
    case 'core':
      return buildCore(patch);
    case 'contact-details':
      return buildContactDetails(patch, current);
    case 'tags':
      return buildTags(patch);
    case 'groups':
      return buildGroups(patch, current);
    case 'locations':
      return buildLocations(patch);
    case 'rates':
      return buildRates(patch, current);
    case 'custom-field':
      return buildCustomField(patch, current);
    case 'custom-field-group':
      return buildCustomFieldGroup(patch, current);
  }
}

function buildCore(patch: ResourcePatchOf<'core'>): ContactUpdateBody {
  if (patch.changes.length === 0) return { type: 'no-op' };
  return { type: 'scalar', body: applyCoreChanges({}, patch.changes) };
}

function buildContactDetails(
  patch: ResourcePatchOf<'contact-details'>,
  current: Resource
): ContactUpdateBody {
  const rows: ContactDetailBodyRow[] = patch.items.map(item => ({
    ...optionalIdProps(item.id, `buildUpdateBody(${patch.detailType})`),
    contact_id: current.id,
    detail_type: patch.detailType,
    value_1: item.value,
    value_2: item.type,
    ...(item.country ? { value_3: item.country } : {})
  }));
  switch (patch.detailType) {
    case 'EMAIL':
      return { type: 'emails', body: { emails: rows } };
    case 'PHONE':
      return { type: 'phone_numbers', body: { phone_numbers: rows } };
    case 'SOCIAL':
      return { type: 'socials', body: { socials: rows } };
    case 'WEBSITE':
      return { type: 'websites', body: { websites: rows } };
  }
}

function buildTags(patch: ResourcePatchOf<'tags'>): ContactUpdateBody {
  const tags: TagBodyRow[] = [
    ...patch.ids.map(id => ({
      id: assertPersistedId(id, 'buildUpdateBody(tags)')
    })),
    ...patch.create.map(tag => ({ tag, tag_type: 'resource' as const }))
  ];
  return { type: 'tags', body: { tags } };
}

function buildGroups(
  patch: ResourcePatchOf<'groups'>,
  current: Resource
): ContactUpdateBody {
  const resourceTypeId = current.resource_type_id ?? null;
  if (patch.create.length > 0 && resourceTypeId === null) {
    throw new Error(
      'buildUpdateBody(groups): cannot create groups for a resource without resource_type_id'
    );
  }
  const groups: GroupBodyRow[] = [
    ...patch.ids.map(id => ({
      id: assertPersistedId(id, 'buildUpdateBody(groups)')
    })),
    ...patch.create.map(name => ({
      name,
      // Narrowed above: creating requires a resource type.
      entity_id: resourceTypeId as number
    }))
  ];
  return { type: 'groups', body: { groups } };
}

function buildLocations(
  patch: ResourcePatchOf<'locations'>
): ContactUpdateBody {
  const locations: LocationBodyRow[] = [
    ...patch.existingIds.map(id => ({
      id: assertPersistedId(id, 'buildUpdateBody(locations)')
    })),
    ...patch.create.map(locationRow)
  ];
  return { type: 'locations', body: { locations } };
}

function buildRates(
  patch: ResourcePatchOf<'rates'>,
  current: Resource
): ContactUpdateBody {
  const rates: ContactRateBodyRow[] = patch.items.map((item, index) => {
    const context = `buildUpdateBody(rates)[${index}]`;
    return {
      ...optionalIdProps(item.id, context),
      contact_id: current.id,
      ...rateValueColumns(item, context),
      order: index + 1
    };
  });
  return { type: 'rates', body: { rates } };
}

/**
 * Same-scope row of `definitionId` (archived included, for unique-slot reuse)
 * plus `{ id }` stubs for every other persisted row so Objection keeps them.
 */
function splitCustomFields(
  rows: CurrentCustomField[],
  definitionId: number,
  workspaceId: number | null
): { sameScope: CurrentCustomField | null; stubs: KeepAliveStub[] } {
  const candidates = rows.filter(
    row => row.custom_field_definition_id === definitionId
  );
  const sameScope = findSameScopeCustomFieldRow(candidates, workspaceId);
  const stubs: KeepAliveStub[] = rows
    .filter(
      row => row.id != null && (sameScope === null || row.id !== sameScope.id)
    )
    .map(row => ({ id: row.id }));
  return { sameScope, stubs };
}

function buildCustomField(
  patch: ResourcePatchOf<'custom-field'>,
  current: Resource
): ContactUpdateBody {
  const workspaceId = valueScopeUtils.toWorkspaceId(patch.scope);
  const { sameScope, stubs } = splitCustomFields(
    current.custom_fields ?? [],
    patch.definitionId,
    workspaceId
  );

  if (patch.write.type === 'clear') {
    if (sameScope === null || sameScope.archived === true) {
      return { type: 'no-op' };
    }
    const archive: CustomFieldArchiveRow = {
      id: sameScope.id,
      custom_field_definition_id: sameScope.custom_field_definition_id,
      enterprise_workspace_id: sameScope.enterprise_workspace_id ?? null,
      archived: true
    };
    return {
      type: 'custom_fields',
      body: { custom_fields: [...stubs, archive] }
    };
  }

  const row: CustomFieldBodyRow = {
    ...(sameScope === null ? {} : { id: sameScope.id }),
    ...customFieldRowWithoutId(
      patch.definitionId,
      workspaceId,
      patch.write.columns
    )
  };
  return { type: 'custom_fields', body: { custom_fields: [...stubs, row] } };
}

function buildCustomFieldGroup(
  patch: ResourcePatchOf<'custom-field-group'>,
  current: Resource
): ContactUpdateBody {
  const workspaceId = valueScopeUtils.toWorkspaceId(patch.scope);
  const currentGroups = current.custom_fieldgroups ?? [];
  const referencedIds = new Set<number>();
  for (const row of patch.rows) {
    if (row.id !== null) {
      referencedIds.add(
        assertPersistedId(row.id, 'buildUpdateBody(custom-field-group)')
      );
    }
  }

  // Keep alive: rows of other definitions, and archived rows of this
  // definition. Live rows of this definition missing from the patch are
  // intentionally omitted (the backend deletes them).
  const stubs: KeepAliveStub[] = currentGroups
    .filter(
      group =>
        group.id != null &&
        !referencedIds.has(group.id) &&
        (group.custom_fieldgroup_definition_id !== patch.definitionId ||
          group.archived === true)
    )
    .map(group => ({ id: group.id }));

  const rows = patch.rows.map((row, index) =>
    buildGroupRow(row, index, patch.definitionId, workspaceId, currentGroups)
  );

  return {
    type: 'custom_fieldgroups',
    body: { custom_fieldgroups: [...stubs, ...rows] }
  };
}

function buildGroupRow(
  row: GroupRowWrite,
  index: number,
  definitionId: number,
  workspaceId: number | null,
  currentGroups: CurrentCustomFieldGroup[]
): CustomFieldGroupBodyRow {
  const context = `buildUpdateBody(custom-field-group ${definitionId})`;
  const currentRow = findCurrentGroupRow(
    row,
    definitionId,
    currentGroups,
    context
  );
  const currentMembers =
    currentRow === null ? [] : (currentRow.custom_fields ?? []);
  const usedIds = new Set<number>();

  const members: CustomFieldBodyRow[] = row.members.map(member => {
    const candidates = currentMembers.filter(
      field => field.custom_field_definition_id === member.definitionId
    );
    const sameScope = findSameScopeCustomFieldRow(candidates, workspaceId);
    let id: number | null;
    if (member.id !== null) {
      id = assertPersistedId(member.id, context);
      if (!currentMembers.some(field => field.id === id)) {
        throw new Error(
          `${context}: member id ${id} is not a row of group ${row.id === null ? '(new)' : row.id}`
        );
      }
    } else {
      id = sameScope === null ? null : sameScope.id;
    }
    if (id !== null) usedIds.add(id);
    return {
      ...(id === null ? {} : { id }),
      ...customFieldRowWithoutId(
        member.definitionId,
        workspaceId,
        member.columns
      )
    };
  });

  // Members not addressed by this write (other scopes, other definitions,
  // archived) stay alive as stubs — the nested relation is set-list too.
  const memberStubs: KeepAliveStub[] = currentMembers
    .filter(field => field.id != null && !usedIds.has(field.id))
    .map(field => ({ id: field.id }));

  return {
    ...(row.id === null ? {} : { id: row.id }),
    custom_fieldgroup_definition_id: definitionId,
    archived: false,
    order: index + 1,
    custom_fields: [...memberStubs, ...members]
  };
}

function findCurrentGroupRow(
  row: GroupRowWrite,
  definitionId: number,
  currentGroups: CurrentCustomFieldGroup[],
  context: string
): CurrentCustomFieldGroup | null {
  if (row.id === null) return null;
  const found = currentGroups.find(group => group.id === row.id) ?? null;
  if (found === null) {
    // Without the current members we cannot emit keep-alive stubs; sending
    // the row would delete every member not in the write.
    throw new Error(
      `${context}: group row ${row.id} is not on the current resource`
    );
  }
  if (found.custom_fieldgroup_definition_id !== definitionId) {
    throw new Error(
      `${context}: group row ${row.id} belongs to definition ${found.custom_fieldgroup_definition_id}`
    );
  }
  return found;
}
